// ============================================================
// TUTORIAL CONFIGURATION
// Generated by Learn by Building Tutorial Generator
// Date: 2026-02-07
// Source: Video recording - "Adding a video response question"
// ============================================================
// 
// NOTE: This example demonstrates the CHALLENGE-BASED writing style:
// - title: What to accomplish (not "click X")
// - instruction: Context and goal (not specific UI directions)
// - hint: Specific location guidance (shown only when user requests)
//
// The intro screen will display:
// - config.title as the main heading
// - config.description as the explanation
// - config.estimatedTime and steps count as stats
// ============================================================

// ------------------------------------------------------------
// TUTORIAL METADATA
// ------------------------------------------------------------
const tutorialConfig = {
  // Unique identifier - must match filename and screenshot folder
  id: 'add-video-response-question',
  
  // Title shown on intro screen and header during tutorial
  title: 'Adding a Video Response Question',
  
  // Description shown on intro screen - explain what they'll learn
  description: 'Practice how to add a video response question to your survey, configure recording options, and customize the question settings to capture video feedback from respondents.',
  
  // Time estimate shown on intro screen
  estimatedTime: '3 minutes',
  
  // Difficulty badge
  difficulty: 'Beginner',
  
  // For future use
  prerequisites: [],
  tags: ['survey', 'question-types', 'video-response', 'feedback', 'multimedia'],
  
  // UI accent color (buttons, progress bar, badges)
  brandColor: '#0066CC',
};

// ------------------------------------------------------------
// TUTORIAL STEPS
// ------------------------------------------------------------
const steps = [
  {
    id: 1,
    // CHALLENGE-STYLE: States WHAT to do, not HOW
    title: "Let\'s add a new question",
    // CONTEXT: Why we're doing this, not where to click
    instruction: "We want to add a video response question to this survey to capture video feedback from survey respondents.",
    screenshot: '/screenshots/add-video-response-question/step-1.png',
    hotspot: {
      top: 50.9,
      left: 88.1,
      width: 10,
      height: 5.5,
    },
    // HINT: Specific UI guidance (only shown when requested)
    hint: 'Look for the blue "+ Add new question" button in the bottom-right corner of the survey builder.',
    wrongClickMessage: 'Not quite! Find the blue "+ Add new question" button at the bottom-right of the screen.',
    requiresInput: false,
  },
  {
    id: 2,
    title: "Select the correct question type",
    instruction: "From the question type menu, we need to choose the option that allows respondents to record video responses.",
    screenshot: '/screenshots/add-video-response-question/step-2.png',
    hotspot: {
      top: 64.2,
      left: 80.6,
      width: 12.5,
      height: 3.5,
    },
    hint: 'In the question type dropdown menu, scroll down and look for "Video response" - it has a video camera icon.',
    wrongClickMessage: 'That\'s not the video response option. Look for "Video response" in the dropdown menu.',
    requiresInput: false,
  },
  {
    id: 3,
    title: "Enable respondents to choose between recording in real time or uploading a recording from their device",
    instruction: "Let\'s allow respondents to upload a pre-recorded video file instead of only recording live.",
    screenshot: '/screenshots/add-video-response-question/step-3.png',
    hotspot: {
      top: 28.3,
      left: 4.9,
      width: 13.5,
      height: 3,
    },
    hint: 'In the left panel under "Recording options", find the toggle switch for "Option to upload response" and click it to enable.',
    wrongClickMessage: 'Look in the left sidebar under "Recording options" for the "Option to upload response" toggle.',
    requiresInput: false,
  },
  {
    id: 4,
    title: "Enable respondents choose between recording video or recording audio only",
    instruction: "Let\'s give respondents the flexibility to record audio-only responses if they prefer not to use video.",
    screenshot: '/screenshots/add-video-response-question/step-4.png',
    hotspot: {
      top: 33.2,
      left: 4.9,
      width: 13.5,
      height: 3,
    },
    hint: 'Still in the "Recording options" section, find the toggle for "Option to record audio only" and enable it.',
    wrongClickMessage: 'Look for the "Option to record audio only" toggle in the left sidebar under "Recording options".',
    requiresInput: false,
  },
  {
    id: 5,
    title: "Set a recording time limit",
    instruction: "To keep responses focused and manageable, let\'s set a maximum recording length for video responses.",
    screenshot: '/screenshots/add-video-response-question/step-5.png',
    hotspot: {
      top: 42.8,
      left: 4.9,
      width: 13.5,
      height: 3,
    },
    hint: 'Under the "Format" section in the left panel, find the "Recording time limit" toggle and click to enable it.',
    wrongClickMessage: 'Look in the left sidebar under "Format" for the "Recording time limit" toggle switch.',
    requiresInput: false,
  },
  {
    id: 6,
    title: "Add a \'Confidentiality Statement\'",
    instruction: "Let\'s add an assurance of confidentiality to make respondents feel more comfortable",
    screenshot: '/screenshots/add-video-response-question/step-7.png',
    hotspot: {
      top: 84.9,
      left: 88.1,
      width: 10,
      height: 5,
    },
    hint: 'Look for the blue "+ Add new question" button below the current question in the survey builder.',
    wrongClickMessage: 'Find the "+ Add new question" button below the video response question you just created.',
    requiresInput: false,
  },
  {
    id: 7,
    title: "Choose the correct question type to add this text",
    instruction: "For this next question, let\'s select a question type that allows you to add your confidentiality statement text",
    screenshot: '/screenshots/add-video-response-question/step-8.png',
    hotspot: {
      top: 61.8,
      left: 80.6,
      width: 13,
      height: 3.5,
    },
    hint: 'In the question type dropdown that appears, look for "Text / Graphic" option near the middle of the list.',
    wrongClickMessage: 'That\'s not the right option. Look for "Text / Graphic" in the question type menu.',
    requiresInput: false,
  },
  {
    id: 8,
    title: "Let\'s publish your survey",
    instruction: "It\'s all set! Let's publish and distribute this survey",
    screenshot: '/screenshots/add-video-response-question/step-9.png',
    hotspot: {
      top: 9.2,
      left: 94.2,
      width: 13,
      height: 3.5,
    },
    hint: 'Look for the blue "Publish" button in the upper right corner in the survey builder.',
    wrongClickMessage: 'That\'s not the right option. Look for the blue "Publish" button in the upper right corner',
    requiresInput: false,
  },
];

// ------------------------------------------------------------
// SCREENSHOT CHECKLIST
// ------------------------------------------------------------
/*
All screenshots must have the SAME aspect ratio (recommended: 3418×1906 or equivalent).
Save to: public/screenshots/add-video-response-question/

□ step-1.png: Survey Builder with existing question(s), "+ Add new question" button visible at bottom-right
□ step-2.png: Question type dropdown menu open, showing "Video response" option
□ step-3.png: Video response question editor showing left panel with "Option to upload response" toggle
□ step-4.png: Left panel showing "Option to record audio only" toggle
□ step-5.png: Left panel showing "Recording time limit" toggle under Format section
□ step-6.png: Video response question visible with "+ Add new question" button below it
□ step-7.png: Question type dropdown showing "Text / Graphic" option
*/

// ------------------------------------------------------------
// EXPORTS
// ------------------------------------------------------------
export { tutorialConfig, steps };
